// Copyright (c) 2026 Theiagen Genomics. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use is prohibited.
// SVG leaf-label-annotations renderer.
//
// Renders a coloured band just beyond the leaves of the clade rooted at
// annotation.nodeId. NO text is emitted — purely a coloured rect or ring sector.

import { TreeTypes } from "../core/constants.js";
import {
  getLeafDescendants,
  estimateTextWidth,
  buildAnnularSectorPath,
} from "./annotations-geometry.js";
import { el } from "./dom.js";

// ---------------------------------------------------------------------------
// Hard defaults (merged last, lowest precedence)
// ---------------------------------------------------------------------------

const HARD_DEFAULTS = {
  strokeWidth: 1,
  opacity: 0.5,
  track: 0,
  visible: true,
};

// ---------------------------------------------------------------------------
// Coordinate helpers
// ---------------------------------------------------------------------------

function radiusOf(n) {
  return Number.isFinite(n.dist) ? n.dist : Math.hypot(n.x, n.y);
}

// ---------------------------------------------------------------------------
// Colour helpers — opacity multiplies the channel alpha
// ---------------------------------------------------------------------------

function fillAttr(color, opacity) {
  const [r, g, b] = color;
  return `rgb(${r},${g},${b})`;
}

function fillOpacityAttr(color, opacity) {
  const a = color[3] !== undefined ? color[3] : 255;
  return opacity * (a / 255);
}

function strokeAttr(color) {
  const [r, g, b] = color;
  return `rgb(${r},${g},${b})`;
}

function strokeOpacityAttr(color, opacity) {
  const a = color[3] !== undefined ? color[3] : 255;
  return opacity * (a / 255);
}

// ---------------------------------------------------------------------------
// Rectangular / diagonal / hierarchical geometry
//
//   longestLeafX = max x over ALL visible leaves
//   minX = longestLeafX + leafLabelPadding + track * trackSpacing
//   maxLabelWidth = max estimateTextWidth over CLADE leaves
//   maxX = minX + maxLabelWidth + 10
//   minY/maxY from clade visible leaves ± padding 5
// ---------------------------------------------------------------------------

const RECT_PADDING = 5;
const OUTWARD_EXTENSION = 10;

function buildRectElement(annotation, node, graph, leafLabelFontSize, leafLabelPadding, trackSpacing) {
  const leaves = node.isLeaf ? [node] : getLeafDescendants(node);
  if (leaves.length === 0) return null;

  const visibleLeaves = leaves.filter(leaf => leaf.id && !leaf.isHidden);
  if (visibleLeaves.length === 0) return null;

  const track = annotation.track || 0;

  const allVisibleLeaves = graph.leaves
    ? graph.leaves.filter(n => !n.isHidden)
    : graph.preorderTraversal.filter(n => n.isLeaf && !n.isHidden);

  const longestLeafX = Math.max(...allVisibleLeaves.map(n => n.x));

  const minXBase = longestLeafX + leafLabelPadding + track * trackSpacing;

  const maxLabelWidth = Math.max(...visibleLeaves.map(leaf =>
    estimateTextWidth(leaf.id || '', leafLabelFontSize)
  ));

  const minX = minXBase - RECT_PADDING;
  const maxX = minXBase + maxLabelWidth + OUTWARD_EXTENSION + RECT_PADDING;

  const minY = Math.min(...visibleLeaves.map(leaf => leaf.y)) - RECT_PADDING;
  const maxY = Math.max(...visibleLeaves.map(leaf => leaf.y)) + RECT_PADDING;

  return el("rect", {
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY,
    fill: fillAttr(annotation.fillColor, annotation.opacity),
    "fill-opacity": fillOpacityAttr(annotation.fillColor, annotation.opacity),
    stroke: strokeAttr(annotation.strokeColor),
    "stroke-opacity": strokeOpacityAttr(annotation.strokeColor, annotation.opacity),
    "stroke-width": annotation.strokeWidth,
    "data-node-id": node.id,
    "data-annotation-id": annotation.id,
  });
}

// ---------------------------------------------------------------------------
// Circular / radial geometry
//
//   maxLeafDistance = max radiusOf over ALL visible leaves
//   innerRadius = maxLeafDistance + leafLabelPadding + track * trackSpacing
//   maxLabelWidth = max estimateTextWidth over CLADE leaves
//   outerRadius = innerRadius + maxLabelWidth
//   anglePadding = 0.02 rad; start = minAngle - 0.02, end = maxAngle + 0.02
// ---------------------------------------------------------------------------

const ANGLE_PADDING = 0.02;

function buildSectorElement(annotation, node, graph, leafLabelFontSize, leafLabelPadding, trackSpacing) {
  const leaves = node.isLeaf ? [node] : getLeafDescendants(node);
  if (leaves.length === 0) return null;

  const visibleLeaves = leaves.filter(leaf => !leaf.isHidden);
  if (visibleLeaves.length === 0) return null;

  const track = annotation.track || 0;

  const allVisibleLeaves = graph.leaves
    ? graph.leaves.filter(n => !n.isHidden)
    : graph.preorderTraversal.filter(n => n.isLeaf && !n.isHidden);

  const maxLeafDistance = Math.max(...allVisibleLeaves.map(radiusOf));

  const innerRadius = maxLeafDistance + leafLabelPadding + track * trackSpacing;

  const maxLabelWidth = Math.max(...visibleLeaves.map(leaf =>
    estimateTextWidth(leaf.id || '', leafLabelFontSize)
  ));

  const outerRadius = innerRadius + maxLabelWidth;

  const angles = visibleLeaves.map(leaf => leaf.angle);
  const minAngle = Math.min(...angles);
  const maxAngle = Math.max(...angles);

  const startAngle = minAngle - ANGLE_PADDING;
  const endAngle   = maxAngle + ANGLE_PADDING;

  const d = buildAnnularSectorPath(startAngle, endAngle, innerRadius, outerRadius);

  return el("path", {
    d,
    fill: fillAttr(annotation.fillColor, annotation.opacity),
    "fill-opacity": fillOpacityAttr(annotation.fillColor, annotation.opacity),
    stroke: strokeAttr(annotation.strokeColor),
    "stroke-opacity": strokeOpacityAttr(annotation.strokeColor, annotation.opacity),
    "stroke-width": annotation.strokeWidth,
    "data-node-id": node.id,
    "data-annotation-id": annotation.id,
  });
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Render leaf-label annotation bands as an SVG <g> element.
 *
 * Each annotation produces one coloured <rect> (rectangular/diagonal/hierarchical
 * layouts) or <path> (circular/radial layouts) positioned just beyond the leaf
 * labels of the annotated clade. No text is emitted.
 *
 * @param {object} graph - Built graph (graph.nodeById, graph.leaves, graph.preorderTraversal).
 * @param {object} props - Rendering props.
 * @returns {SVGGElement|null}
 */
export function renderLeafLabelAnnotations(graph, props) {
  const rawAnnotations = props.leafLabelAnnotations;
  if (!rawAnnotations || rawAnnotations.length === 0) return null;
  if (!graph || !graph.nodeById) return null;

  const userDefaults = props.leafLabelAnnotationDefaults || {};
  const leafLabelFontSize = props.leafLabelFontSize ?? 12;
  const leafLabelPadding  = props.leafLabelPadding ?? 5;
  const trackSpacing      = props.leafLabelAnnotationTrackSpacing ?? 20;

  const treeType = props.type;
  const isCircular = treeType === TreeTypes.Circular || treeType === TreeTypes.Radial;

  const elements = [];

  for (const raw of rawAnnotations) {
    const annotation = { ...HARD_DEFAULTS, ...userDefaults, ...raw };

    if (!annotation.visible) continue;
    if (!annotation.id || !annotation.nodeId) continue;
    if (!annotation.fillColor || !annotation.strokeColor) continue;

    const node = graph.nodeById[annotation.nodeId];
    if (!node || node.isHidden) continue;

    const shape = isCircular
      ? buildSectorElement(annotation, node, graph, leafLabelFontSize, leafLabelPadding, trackSpacing)
      : buildRectElement(annotation, node, graph, leafLabelFontSize, leafLabelPadding, trackSpacing);

    if (shape) {
      elements.push(shape);
    }
  }

  if (elements.length === 0) return null;

  return el("g", { class: "leaf-label-annotations" }, elements);
}
